import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { formatPriceLakh } from "@/lib/format";

const STATUSES = ["new", "contacted", "qualified", "visit_booked", "test_drive", "negotiation", "sold", "lost"];

export default function LeadsPage({ sourceFilter }) {
  const [params] = useSearchParams();
  const effectiveSource = sourceFilter || params.get("source") || "";
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);

  const load = () => {
    setLoading(true);
    const q = new URLSearchParams();
    if (effectiveSource) q.set("source", effectiveSource);
    if (status) q.set("status", status);
    api.get(`/leads?${q.toString()}`).then((r) => setItems(r.data.items)).finally(() => setLoading(false));
  };
  useEffect(load, [effectiveSource, status]);

  const update = async (id, patch) => { await api.patch(`/leads/${id}`, patch); load(); };

  const titleMap = { sell_car: "Sell Car Requests", test_drive: "Test Drives" };
  const title = titleMap[effectiveSource] || "Leads";

  return (
    <div className="space-y-5" data-testid={`admin-${title.toLowerCase().replace(/\s+/g, "-")}-page`}>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display font-extrabold text-2xl text-gray-900">{title}</h1>
          <p className="text-sm text-gray-500 mt-1">{items.length} records</p>
        </div>
        <div className="flex gap-2">
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-md border border-gray-200 px-3 py-2 text-sm">
            <option value="">All status</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wider">
            <tr>
              <th className="p-3 text-left">Date</th>
              <th className="p-3 text-left">Name</th>
              <th className="p-3 text-left">Phone</th>
              {!effectiveSource && <th className="p-3 text-left">Source</th>}
              <th className="p-3 text-left">Context</th>
              <th className="p-3 text-left">Status</th>
              <th className="p-3 text-left">Notes</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? <tr><td colSpan={7} className="p-10 text-center text-gray-400">Loading…</td></tr>
              : items.length === 0 ? <tr><td colSpan={7} className="p-10 text-center text-gray-400">No records.</td></tr>
              : items.map((l) => (
                <>
                  <tr key={l.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => setOpen(open === l.id ? null : l.id)}>
                    <td className="p-3 text-xs text-gray-500 whitespace-nowrap">{new Date(l.created_at).toLocaleDateString()}</td>
                    <td className="p-3 font-semibold text-gray-900">{l.name}</td>
                    <td className="p-3"><a href={`tel:${l.phone}`} className="text-[#940C2C]" onClick={(e) => e.stopPropagation()}>{l.phone}</a></td>
                    {!effectiveSource && <td className="p-3"><span className="bg-gray-100 rounded px-2 py-0.5 text-xs">{l.source}</span></td>}
                    <td className="p-3 text-xs text-gray-700 max-w-sm truncate">{l.vehicle_name || `${l.make || ""} ${l.model || ""}`.trim() || l.message || "—"}</td>
                    <td className="p-3" onClick={(e) => e.stopPropagation()}>
                      <select value={l.status} onChange={(e) => update(l.id, { status: e.target.value })} className="rounded border border-gray-200 px-2 py-1 text-xs">
                        {STATUSES.map((s) => <option key={s}>{s}</option>)}
                      </select>
                    </td>
                    <td className="p-3 text-xs text-gray-500">{l.notes ? l.notes.slice(0, 30) + (l.notes.length > 30 ? "…" : "") : "—"}</td>
                  </tr>
                  {open === l.id && (
                    <tr>
                      <td colSpan={7} className="bg-gray-50 p-5 border-t border-b border-gray-100">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                          <div className="space-y-2 text-sm">
                            {l.email && <Row label="Email" value={l.email} />}
                            {l.vehicle_name && <Row label="Vehicle" value={l.vehicle_name} />}
                            {l.make && <Row label="Make/Model" value={`${l.make} ${l.model || ""} ${l.year || ""}`} />}
                            {l.registration_number && <Row label="Reg Number" value={l.registration_number} />}
                            {l.location && <Row label="Location" value={l.location} />}
                            {l.preferred_date && <Row label="Preferred Date" value={`${l.preferred_date} ${l.preferred_time || ""}`} />}
                            {l.loan_amount && <Row label="Loan Amount" value={formatPriceLakh(l.loan_amount)} />}
                            {l.message && <Row label="Message" value={l.message} />}
                          </div>
                          <div>
                            <label className="text-xs font-semibold text-gray-600">Internal Notes</label>
                            <textarea defaultValue={l.notes || ""} rows={3} onBlur={(e) => update(l.id, { notes: e.target.value })} className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm" />
                            <label className="text-xs font-semibold text-gray-600 mt-3 block">Follow-up Date</label>
                            <input type="date" defaultValue={l.follow_up_date || ""} onBlur={(e) => update(l.id, { follow_up_date: e.target.value })} className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm" />
                            <label className="text-xs font-semibold text-gray-600 mt-3 block">Assigned To</label>
                            <input defaultValue={l.assigned_to || ""} placeholder="Salesperson name" onBlur={(e) => update(l.id, { assigned_to: e.target.value })} className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm" />
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const Row = ({ label, value }) => (
  <div className="flex gap-2 text-sm"><span className="font-semibold text-gray-500 w-28 shrink-0">{label}</span><span className="text-gray-900">{value}</span></div>
);
